import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface FormField {
  id: string
  type: string
  label: string
  required?: boolean
  options?: Array<{ label: string; value: string }>
  placeholder?: string
  help_text?: string
  validation?: {
    pattern?: string
    message?: string
    min?: number
    max?: number
  }
}

interface FormSection {
  id: string
  title: string
  order: number
  fields: FormField[]
  collapsible?: boolean
  collapsed_by_default?: boolean
}

interface FormConfig {
  sections: FormSection[]
}

interface Patient {
  id: number
  patient_id: string
  medical_record_number: string
  first_name: string
  last_name: string
  full_name: string
  age: number
  date_of_birth: string
  gender: 'M' | 'F' | 'O' | 'U'
  phone: string
  email: string
  address: string
  emergency_contact: string
  emergency_phone: string
  insurance_number: string
  allergies: string
  medical_history: string
  is_active: boolean
  created_at: string
  updated_at: string
  custom_data?: Record<string, any>
  [key: string]: any // For dynamic custom fields
}

const PatientManagementDynamic: React.FC = () => {
  const { t } = useTranslation()
  const [patients, setPatients] = useState<Patient[]>([])
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null)
  const [deleteInfo, setDeleteInfo] = useState<{
    cases_count: number
    slides_count: number
    reports_count: number
    has_related_data: boolean
  } | null>(null)

  // Form template state
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})

  // Load form template
  const loadFormTemplate = async () => {
    try {
      const response = await fetch('/api/v1/pathology/patient-form-templates/')
      if (response.ok) {
        const data = await response.json()
        const defaultTemplate = data.results?.find((t: any) => t.is_default) || data.results?.[0]
        if (defaultTemplate) {
          setFormConfig(defaultTemplate.form_config)
        }
      }
    } catch (err) {
      console.error('Error loading form template:', err)
    }
  }

  const fetchPatients = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/pathology/patients/')

      if (!response.ok) {
        throw new Error('Failed to fetch patients')
      }

      const data = await response.json()
      setPatients(data.results || data)
    } catch (error) {
      console.error('Error fetching patients:', error)
      toast.error('Lỗi tải dữ liệu bệnh nhân')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadFormTemplate()
    fetchPatients()
  }, [])

  const filteredPatients = patients.filter(patient => {
    const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase()
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.medical_record_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm)

    return matchesSearch
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFieldValue = (value: any, field: FormField): string => {
    if (value === null || value === undefined || value === '') return '-'

    if (field.type === 'date') {
      return formatDate(value)
    }

    if (field.type === 'checkbox') {
      return value ? 'Có' : 'Không'
    }

    if (field.type === 'select' && field.options) {
      const option = field.options.find(opt => opt.value === value)
      return option?.label || value
    }

    return String(value)
  }

  const getAllFields = (): FormField[] => {
    if (!formConfig) return []
    return formConfig.sections.flatMap(section => section.fields)
  }

  const getFieldValue = (patient: Patient, fieldId: string): any => {
    // Check if it's in root level (standard or flattened custom field)
    if (patient[fieldId] !== undefined) {
      return patient[fieldId]
    }
    // Check in custom_data
    if (patient.custom_data && patient.custom_data[fieldId] !== undefined) {
      return patient.custom_data[fieldId]
    }
    return null
  }

  const handleEditPatient = async () => {
    if (!selectedPatient) return

    try {
      const response = await fetch(`/api/v1/pathology/patients/${selectedPatient.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Đã cập nhật')
        setShowEditModal(false)
        setSelectedPatient(null)
        setFormData({})
        fetchPatients()
      } else {
        const errorData = await response.json()
        toast.error(`Lỗi: ${errorData.detail || JSON.stringify(errorData)}`)
      }
    } catch (error) {
      console.error('Error updating patient:', error)
      toast.error('Lỗi cập nhật')
    }
  }

  const handleDeletePatient = async () => {
    if (!patientToDelete) return

    try {
      const response = await fetch(`/api/v1/pathology/patients/${patientToDelete.id}/`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Đã xóa bệnh nhân')
        setShowDeleteModal(false)
        setPatientToDelete(null)
        fetchPatients()
      } else {
        const errorData = await response.json()
        toast.error(`Lỗi: ${errorData.detail || errorData.error || 'Không thể xóa bệnh nhân'}`, {
          duration: 5000
        })
        setShowDeleteModal(false)
        setPatientToDelete(null)
      }
    } catch (error) {
      console.error('Error deleting patient:', error)
      toast.error('Lỗi kết nối khi xóa bệnh nhân')
      setShowDeleteModal(false)
      setPatientToDelete(null)
    }
  }

  const handleViewPatient = (patient: Patient) => {
    setSelectedPatient(patient)
    setShowPatientModal(true)
  }

  const openEditModal = (patient: Patient) => {
    setSelectedPatient(patient)

    // Initialize form data with all fields from patient
    const initialData: Record<string, any> = {}

    getAllFields().forEach(field => {
      const value = getFieldValue(patient, field.id)
      initialData[field.id] = value !== null ? value : (field.type === 'checkbox' ? false : '')
    })

    setFormData(initialData)
    setShowEditModal(true)
  }

  const openDeleteModal = async (patient: Patient) => {
    setPatientToDelete(patient)

    try {
      const response = await fetch(`/api/v1/pathology/patients/${patient.id}/delete_info/`)
      if (response.ok) {
        const info = await response.json()
        setDeleteInfo(info)
      }
    } catch (error) {
      console.error('Error fetching delete info:', error)
    }

    setShowDeleteModal(true)
  }

  const renderFieldInput = (field: FormField) => {
    const value = formData[field.id] || ''
    const handleChange = (newValue: any) => {
      setFormData(prev => ({ ...prev, [field.id]: newValue }))
    }

    const inputClasses = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.type === 'phone' ? 'tel' : field.type}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClasses}
            placeholder={field.placeholder}
            required={field.required}
          />
        )

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClasses}
            placeholder={field.placeholder}
            rows={2}
            required={field.required}
          />
        )

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClasses}
            required={field.required}
          />
        )

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClasses}
            required={field.required}
          >
            <option value="">-- Chọn --</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )

      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">{field.label}</span>
          </div>
        )

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClasses}
            required={field.required}
          />
        )
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Bệnh nhân</h1>
            <p className="text-gray-600 text-sm mt-1">Quản lý thông tin bệnh nhân (Dynamic Form)</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Tìm theo tên, mã bệnh nhân, mã hồ sơ, số điện thoại..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={fetchPatients}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            Làm mới
          </button>
        </div>
      </div>

      {/* Patients Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mã BN</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Họ và tên</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày sinh</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Giới tính</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Số điện thoại</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thao tác</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPatients.map((patient) => (
              <tr key={patient.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {patient.patient_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{patient.full_name}</div>
                  {patient.medical_record_number && (
                    <div className="text-xs text-gray-500">MRN: {patient.medical_record_number}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>{formatDate(patient.date_of_birth)}</div>
                  <div className="text-xs text-gray-500">{patient.age} tuổi</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {patient.gender === 'M' ? 'Nam' : patient.gender === 'F' ? 'Nữ' : 'Khác'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {patient.phone}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                  <button
                    onClick={() => handleViewPatient(patient)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Xem
                  </button>
                  <button
                    onClick={() => openEditModal(patient)}
                    className="text-yellow-600 hover:text-yellow-900"
                  >
                    Sửa
                  </button>
                  <Link
                    to={`/pathology/patients/${patient.id}/history`}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Lịch sử
                  </Link>
                  <button
                    onClick={() => openDeleteModal(patient)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Xóa
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredPatients.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy bệnh nhân</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Thử điều chỉnh từ khóa tìm kiếm' : 'Hãy thêm bệnh nhân đầu tiên'}
            </p>
          </div>
        )}
      </div>

      {/* Patient Details Modal - DYNAMIC */}
      {showPatientModal && selectedPatient && formConfig && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Thông tin bệnh nhân (Dynamic)</h3>
              <button
                onClick={() => setShowPatientModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Render sections dynamically */}
            <div className="space-y-6">
              {formConfig.sections
                .sort((a, b) => a.order - b.order)
                .map(section => (
                  <div key={section.id} className="border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">{section.title}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {section.fields.map(field => {
                        const value = getFieldValue(selectedPatient, field.id)
                        if (!value && value !== false && value !== 0) return null // Skip empty fields

                        return (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                            <p className="text-sm text-gray-900">{formatFieldValue(value, field)}</p>
                            {field.help_text && (
                              <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                <div>
                  <span className="font-medium">Ngày tạo:</span> {formatDateTime(selectedPatient.created_at)}
                </div>
                <div>
                  <span className="font-medium">Cập nhật:</span> {formatDateTime(selectedPatient.updated_at)}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowPatientModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Đóng
              </button>
              <button
                onClick={() => { setShowPatientModal(false); openEditModal(selectedPatient) }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Chỉnh sửa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - DYNAMIC */}
      {showEditModal && selectedPatient && formConfig && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Chỉnh sửa thông tin (Dynamic)</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Render sections dynamically */}
            <div className="space-y-6">
              {formConfig.sections
                .sort((a, b) => a.order - b.order)
                .map(section => (
                  <div key={section.id} className="border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">
                      {section.title}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {section.fields.map(field => (
                        <div key={field.id} className={field.type === 'textarea' ? 'col-span-2' : ''}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {renderFieldInput(field)}
                          {field.help_text && (
                            <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleEditPatient}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">⚠️ Xác nhận xóa bệnh nhân</h3>

              <div className="bg-blue-50 p-4 rounded-md mb-4 border border-blue-200">
                <p className="text-base font-bold text-gray-900">{patientToDelete.full_name}</p>
                <p className="text-sm text-gray-600">Mã BN: {patientToDelete.patient_id}</p>
              </div>

              {deleteInfo && deleteInfo.has_related_data && (
                <div className="bg-red-50 p-4 rounded-md mb-4 border-2 border-red-300">
                  <p className="text-sm font-bold text-red-800 mb-3">
                    🚨 Xóa bệnh nhân sẽ xóa TẤT CẢ dữ liệu liên quan:
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {deleteInfo.cases_count > 0 && (
                      <div className="bg-white p-2 rounded">
                        <div className="text-2xl font-bold text-red-600">{deleteInfo.cases_count}</div>
                        <div className="text-xs text-gray-600">Hồ sơ</div>
                      </div>
                    )}
                    {deleteInfo.slides_count > 0 && (
                      <div className="bg-white p-2 rounded">
                        <div className="text-2xl font-bold text-red-600">{deleteInfo.slides_count}</div>
                        <div className="text-xs text-gray-600">Ảnh slide</div>
                      </div>
                    )}
                    {deleteInfo.reports_count > 0 && (
                      <div className="bg-white p-2 rounded">
                        <div className="text-2xl font-bold text-red-600">{deleteInfo.reports_count}</div>
                        <div className="text-xs text-gray-600">Báo cáo</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p className="text-sm text-red-700 font-semibold mb-2">
                ❌ Hành động này KHÔNG THỂ hoàn tác!
              </p>

              <div className="flex justify-center space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setDeleteInfo(null)
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeletePatient}
                  className="px-6 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 font-bold"
                >
                  Xác nhận xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PatientManagementDynamic
